import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import tokenStore from '../utils/tokenStore';
import { API_URL } from '../constants/api';
import { useToast } from './ToastContext';
import { appendFocusLog } from '../utils/focusLog';

const TimerContext = createContext();

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

// Simple completion chime using Web Audio API (no file needed)
const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.5);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.5);
    });
  } catch (e) { /* silent fallback */ }
};

export const TimerProvider = ({ children }) => {
  const [timerState, setTimerState] = useState(() => {
    const saved = localStorage.getItem('globalTimerState');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.isActive && parsed.startTime) {
          const elapsed = Math.floor((Date.now() - parsed.startTime) / 1000);
          const remaining = Math.max(0, parsed.initialDuration - elapsed);
          if (remaining > 0) {
            return { ...parsed, timeRemaining: remaining };
          }
        }
        return {
          timeRemaining: 1500, isActive: false, startTime: null, initialDuration: 1500,
          taskName: '', blockId: null, blockTitle: null, issueId: null, issueTitle: null,
          isBreak: false,
        };
      } catch (e) { /* fallthrough */ }
    }
    return {
      timeRemaining: 1500, isActive: false, startTime: null, initialDuration: 1500,
      taskName: '', blockId: null, blockTitle: null, issueId: null, issueTitle: null,
      isBreak: false,
    };
  });

  // Daily session counter (persisted per day)
  const [sessionsToday, setSessionsToday] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return parseInt(localStorage.getItem(`focusSessions_${today}`) || '0', 10);
  });

  const intervalRef = useRef(null);
  const completionRef = useRef(null);
  const backendURL = API_URL;
  const { showToast } = useToast();

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('globalTimerState', JSON.stringify(timerState));
  }, [timerState]);

  // Persist session count
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`focusSessions_${today}`, String(sessionsToday));
  }, [sessionsToday]);

  // Handle completion side effects safely outside state updater
  const handleCompletionSideEffects = useCallback((data) => {
    try { playChime(); } catch (e) { /* silent on mobile */ }
    try { if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } catch (e) { /* silent */ }

    saveCompletedSession(data.sessionData);

    // Local mirror so the session shows in "Today's Schedule → Focus log"
    // right away, even if the POST above is slow or fails.
    try {
      appendFocusLog({
        kind: 'session',
        title: data.taskName || data.sessionData?.blockTitle || data.sessionData?.issueTitle || 'Focus session',
        minutes: data.duration,
      });
    } catch (e) { /* silent */ }

    try {
      window.dispatchEvent(new CustomEvent('timerComplete', { detail: { taskName: data.taskName, duration: data.duration } }));
    } catch (e) { /* silent */ }

    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('Focus session complete', {
          body: `${data.taskName || 'Session'} — ${data.duration}min done`,
          icon: '/favicon.ico',
        });
      }
    } catch (e) { /* silent on mobile */ }

    // In-app toast — fires no matter which page/tab the user is on, since
    // ToastProvider's viewport is mounted at the app root.
    showToast(`🎉 ${data.taskName || 'Focus session'} complete — ${data.duration} min done!`, 'success', { duration: 6000 });
  }, [showToast]);

  // Single tick evaluation — recomputes remaining time purely from the wall clock.
  // Called both by the 1s interval AND on tab refocus, so the displayed time is
  // always correct no matter how long the tab was backgrounded/suspended.
  const evaluateTimer = useCallback(() => {
    completionRef.current = null;

    setTimerState(prev => {
      if (!prev.isActive || !prev.startTime) return prev;
      // Derive remaining time from wall-clock elapsed time (not a naive -1 decrement)
      // so background-tab throttling/suspension can't make the timer drift or "freeze".
      const elapsed = Math.floor((Date.now() - prev.startTime) / 1000);
      const newTimeRemaining = Math.max(0, prev.initialDuration - elapsed);

      if (newTimeRemaining === prev.timeRemaining) return prev;

      if (newTimeRemaining <= 0) {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

        if (prev.isBreak) {
          // Break finished → chime then back to idle
          completionRef.current = 'break';
          return {
            timeRemaining: 1500, isActive: false, startTime: null, initialDuration: 1500,
            taskName: '', blockId: null, blockTitle: null, issueId: null, issueTitle: null,
            isBreak: false,
          };
        }

        // Work session finished → store data for side effects, start break
        const newSessionCount = (parseInt(localStorage.getItem(`focusSessions_${new Date().toISOString().split('T')[0]}`) || '0', 10)) + 1;
        const breakDuration = (newSessionCount % 4 === 0) ? 15 * 60 : 5 * 60;

        completionRef.current = {
          taskName: prev.taskName,
          duration: Math.floor(prev.initialDuration / 60),
          sessionData: { ...prev },
        };

        return {
          timeRemaining: breakDuration, isActive: true, startTime: Date.now(),
          initialDuration: breakDuration, taskName: prev.taskName,
          blockId: null, blockTitle: null, issueId: null, issueTitle: null,
          isBreak: true,
        };
      }

      return { ...prev, timeRemaining: newTimeRemaining };
    });

    // Side effects OUTSIDE state updater — safe on mobile
    if (completionRef.current && completionRef.current !== 'break') {
      handleCompletionSideEffects(completionRef.current);
      completionRef.current = null;
    } else if (completionRef.current === 'break') {
      try { playChime(); } catch (e) { /* silent */ }
      try { if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } catch (e) { /* silent */ }
      showToast('☕ Break\'s over — ready for your next focus session?', 'info', { duration: 6000 });
      completionRef.current = null;
    }
  }, [handleCompletionSideEffects, showToast]);

  // Background interval — depends on isBreak too so interval restarts on work→break transition
  useEffect(() => {
    if (timerState.isActive) {
      intervalRef.current = setInterval(evaluateTimer, 1000);
      return () => {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      };
    }
  }, [timerState.isActive, timerState.isBreak, evaluateTimer]);

  // Resync instantly when the tab regains focus/visibility. Background tabs (esp.
  // on mobile) get their intervals throttled or fully suspended, so on return we
  // can't wait for the next tick — recompute from the wall clock right away so the
  // countdown jumps to the correct remaining time instead of resuming where it froze.
  useEffect(() => {
    const resync = () => {
      if (document.visibilityState === 'visible') evaluateTimer();
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [evaluateTimer]);

  // Increment session counter when work completes
  useEffect(() => {
    const handleComplete = () => {
      setSessionsToday(prev => prev + 1);
    };
    window.addEventListener('timerComplete', handleComplete);
    return () => window.removeEventListener('timerComplete', handleComplete);
  }, []);

  const saveCompletedSession = async (sessionData) => {
    try {
      const token = tokenStore.get();
      if (!token) return;
      await axios.post(`${backendURL}/api/focus-sessions`, {
        duration: Math.floor(sessionData.initialDuration / 60),
        task_name: sessionData.taskName || sessionData.issueTitle || sessionData.blockTitle || 'Focus Session',
        session_type: sessionData.issueId ? 'issue' : sessionData.blockId ? 'time_block' : 'manual',
        time_block_id: sessionData.blockId || null,
        issue_id: sessionData.issueId || null,
        completed_at: new Date().toISOString(),
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    } catch (error) {
      // Fallback to localStorage
      const sessions = JSON.parse(localStorage.getItem('focusSessions') || '[]');
      sessions.push({
        date: new Date().toISOString().split('T')[0],
        duration: Math.floor(sessionData.initialDuration / 60),
        taskName: sessionData.taskName || 'Focus Session',
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem('focusSessions', JSON.stringify(sessions));
    }
  };

  const startTimer = useCallback((durationMinutes = 25, taskName = '', blockId = null, blockTitle = null, issueId = null, issueTitle = null) => {
    const durationSeconds = durationMinutes * 60;
    setTimerState({
      timeRemaining: durationSeconds, isActive: true, startTime: Date.now(),
      initialDuration: durationSeconds, taskName, blockId, blockTitle, issueId, issueTitle,
      isBreak: false,
    });
  }, []);

  const pauseTimer = useCallback(() => {
    setTimerState(prev => ({ ...prev, isActive: false, startTime: null }));
  }, []);

  const resumeTimer = useCallback(() => {
    setTimerState(prev => ({
      ...prev, isActive: true,
      startTime: Date.now() - ((prev.initialDuration - prev.timeRemaining) * 1000),
    }));
  }, []);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setTimerState({
      timeRemaining: 1500, isActive: false, startTime: null, initialDuration: 1500,
      taskName: '', blockId: null, blockTitle: null, issueId: null, issueTitle: null,
      isBreak: false,
    });
  }, []);

  const skipBreak = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setTimerState({
      timeRemaining: 1500, isActive: false, startTime: null, initialDuration: 1500,
      taskName: '', blockId: null, blockTitle: null, issueId: null, issueTitle: null,
      isBreak: false,
    });
  }, []);

  const value = useMemo(() => ({
    timerState, startTimer, pauseTimer, resumeTimer, resetTimer, skipBreak,
    sessionsToday,
  }), [timerState, startTimer, pauseTimer, resumeTimer, resetTimer, skipBreak, sessionsToday]);

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};

